import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { FileText, Shield, Scale, AlertTriangle, BookOpen, Settings, TrendingUp, CheckCircle } from 'lucide-react';

const WhitePaper = () => {
  const sections = [
    { id: 'executive-summary', title: 'Executive Summary', icon: FileText },
    { id: 'introduction', title: '1. Introduction', icon: BookOpen },
    { id: 'purpose', title: '2. Purpose and Scope', icon: Shield },
    { id: 'token-classification', title: '3. Token Classification', icon: Scale },
    { id: 'architecture', title: '4. System Architecture', icon: Settings },
    { id: 'credit-enhancement', title: '5. Credit Enhancement', icon: TrendingUp },
    { id: 'private-accord', title: '6. Private Accord & PAS', icon: FileText },
    { id: 'use-cases', title: '7. Permitted Uses', icon: CheckCircle },
    { id: 'regulatory', title: '8. Regulatory Considerations', icon: Scale },
    { id: 'risks', title: '9. Risk Factors', icon: AlertTriangle },
    { id: 'governance', title: '10. Governance', icon: Settings },
    { id: 'roadmap', title: '11. Roadmap', icon: TrendingUp },
    { id: 'disclaimer', title: '12. Disclaimer', icon: AlertTriangle },
    { id: 'conclusion', title: '13. Conclusion', icon: CheckCircle },
  ];

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="whitepaper-page">
      {/* Header */}
      <header className="border-b border-[#1a2d4a] bg-[#0d1d35] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <h1 className="text-white text-xl tracking-widest font-light">BETHEREUM</h1>
            </Link>
            <div className="h-6 w-px bg-[#1a2d4a]" />
            <span className="text-[#b8860b] text-sm tracking-wider">WHITE PAPER</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/compliance">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">Compliance</Button>
            </Link>
            <Link to="/login">
              <Button size="sm" className="bg-[#b8860b] hover:bg-[#9a7209]">Client Portal</Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12 flex gap-8">
        {/* Sidebar Navigation */}
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <div className="sticky top-24">
            <Card className="bg-[#1a2d4a] border-[#2a3d5a]">
              <CardContent className="p-4">
                <p className="text-[#b8860b] text-xs tracking-wider mb-4">CONTENTS</p>
                <nav className="space-y-1">
                  {sections.map((section) => (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className="flex items-center gap-2 text-sm text-gray-400 hover:text-white py-1.5 transition-colors"
                    >
                      <section.icon className="w-3 h-3" />
                      <span className="truncate">{section.title}</span>
                    </a>
                  ))}
                </nav>
              </CardContent>
            </Card>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 max-w-4xl">
          {/* Title Section */}
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-1 bg-[#b8860b]/20 border border-[#b8860b]/30 rounded-full mb-6">
              <span className="text-[#b8860b] text-xs tracking-widest">OFFICIAL DOCUMENTATION</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-light text-white mb-4 tracking-wide">BETH White Paper</h1>
            <p className="text-gray-400 text-lg">Blockchain-Enabled Documentation Framework for Private Financial Instruments</p>
            <p className="text-gray-500 text-sm mt-4">Last Updated: January 2026</p>
          </div>

          {/* Executive Summary */}
          <section id="executive-summary" className="mb-16">
            <Card className="bg-[#1a2d4a] border-[#2a3d5a]">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <FileText className="w-6 h-6 text-[#b8860b]" />
                  <h2 className="text-2xl font-light text-white">Executive Summary</h2>
                </div>
                <div className="text-gray-300 leading-relaxed space-y-4">
                  <p>
                    BETH is a blockchain-based digital utility designed to support structured financial documentation, 
                    internal credit enhancement frameworks, and private accord instruments used exclusively in 
                    borrower-initiated financial discussions.
                  </p>
                  <div className="p-4 bg-[#0a1628] border-l-4 border-[#b8860b] rounded">
                    <p className="text-white font-medium mb-2">Important Notice:</p>
                    <p className="text-gray-400 text-sm">
                      BETH is not legal tender, not a payment mechanism, not a settlement instrument, and does not 
                      discharge, satisfy, or transfer debt obligations. The platform provides standardized documentation, 
                      internal attestations, and conditional representations intended solely for informational and 
                      negotiation-support purposes.
                    </p>
                  </div>
                  <p>
                    This white paper is drafted to align with regulatory expectations commonly applied by the U.S. 
                    Securities and Exchange Commission (SEC), the Financial Crimes Enforcement Network (FinCEN), 
                    and analogous regulatory bodies.
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Section 1: Introduction */}
          <section id="introduction" className="mb-12">
            <h2 className="text-2xl font-light text-white mb-6 flex items-center gap-3">
              <span className="text-[#b8860b]">1.</span> Introduction
            </h2>
            <div className="text-gray-300 leading-relaxed space-y-4">
              <p>
                Financial restructuring, modification, and alternative performance discussions frequently lack 
                standardized documentation and verifiable audit trails. BETH was developed to provide a 
                technology-enabled framework for generating, managing, and tracking private financial documentation 
                while preserving creditor autonomy and avoiding regulated payment, settlement, or money transmission activity.
              </p>
              <p>
                BETH functions as a digital utility token within a closed or permissioned environment. Its use is 
                limited to accessing platform services and recording documentation-related events.
              </p>
            </div>
          </section>

          {/* Section 2: Purpose and Scope */}
          <section id="purpose" className="mb-12">
            <h2 className="text-2xl font-light text-white mb-6 flex items-center gap-3">
              <span className="text-[#b8860b]">2.</span> Purpose and Scope
            </h2>
            <div className="text-gray-300 leading-relaxed space-y-6">
              <div>
                <h3 className="text-white text-lg mb-3">The BETH platform is designed to:</h3>
                <ul className="space-y-2">
                  {[
                    'Facilitate standardized documentation for private financial discussions',
                    'Support internal credit enhancement assessments',
                    'Enable borrower-generated private accord documentation',
                    'Record issuance, modification, and expiration events on-chain',
                    'Enhance transparency and auditability'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-white text-lg mb-3">The BETH platform expressly does not:</h3>
                <ul className="space-y-2">
                  {[
                    'Transmit value to third parties',
                    'Act as money, currency, or legal tender',
                    'Settle, discharge, or satisfy debts',
                    'Negotiate with creditors on behalf of users',
                    'Provide credit repair, debt settlement, or legal services'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Section 3: Token Classification */}
          <section id="token-classification" className="mb-12">
            <h2 className="text-2xl font-light text-white mb-6 flex items-center gap-3">
              <span className="text-[#b8860b]">3.</span> Token Classification and Utility
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-[#0a1628] border-[#2a3d5a]">
                <CardContent className="p-6">
                  <h3 className="text-[#b8860b] text-sm tracking-wider mb-4">3.1 TOKEN FUNCTION</h3>
                  <p className="text-gray-300 mb-4">BETH is a non-payment digital utility token. Its sole functions are:</p>
                  <ul className="text-gray-400 space-y-2 text-sm">
                    <li>• Gating access to platform features</li>
                    <li>• Metering platform usage</li>
                    <li>• Anchoring documentation events to a blockchain ledger</li>
                  </ul>
                  <div className="mt-4 p-3 bg-[#1a2d4a] rounded text-xs text-gray-500">
                    BETH does not represent equity, profit participation, revenue rights, or ownership interests, 
                    nor does it convey voting rights or expectations of profit derived from the efforts of others.
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-[#0a1628] border-[#2a3d5a]">
                <CardContent className="p-6">
                  <h3 className="text-[#b8860b] text-sm tracking-wider mb-4">3.2 TRANSFERABILITY</h3>
                  <p className="text-gray-300">
                    Any transferability of BETH is incidental to platform operation and does not constitute 
                    payment, remittance, or settlement activity.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Section 4: System Architecture */}
          <section id="architecture" className="mb-12">
            <h2 className="text-2xl font-light text-white mb-6 flex items-center gap-3">
              <span className="text-[#b8860b]">4.</span> System Architecture
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-[#0a1628] border-[#2a3d5a]">
                <CardContent className="p-6">
                  <h3 className="text-[#b8860b] text-sm tracking-wider mb-4">4.1 SMART CONTRACTS</h3>
                  <p className="text-gray-300 mb-4">Smart contracts are utilized to:</p>
                  <ul className="text-gray-400 space-y-2 text-sm">
                    <li>• Generate unique document identifiers</li>
                    <li>• Timestamp issuance and amendments</li>
                    <li>• Enforce internal platform rules</li>
                    <li>• Maintain immutable audit records</li>
                  </ul>
                  <p className="text-gray-500 text-xs mt-4 italic">
                    Smart contracts do not initiate, route, or execute payments, settlements, or creditor remittances.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-[#0a1628] border-[#2a3d5a]">
                <CardContent className="p-6">
                  <h3 className="text-[#b8860b] text-sm tracking-wider mb-4">4.2 INTERNAL ACCOUNTING</h3>
                  <p className="text-gray-300">
                    All representations of credit enhancement or guarantees remain internal to the platform 
                    and are recorded solely for transparency, monitoring, and audit purposes.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Section 5: Credit Enhancement Framework */}
          <section id="credit-enhancement" className="mb-12">
            <h2 className="text-2xl font-light text-white mb-6 flex items-center gap-3">
              <span className="text-[#b8860b]">5.</span> Credit Enhancement Framework
            </h2>
            <Card className="bg-[#1a2d4a] border-[#2a3d5a]">
              <CardContent className="p-8">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-[#b8860b] text-sm tracking-wider mb-3">5.1 DEFINITION</h3>
                    <p className="text-gray-300">
                      Within the BETH ecosystem, a credit enhancement refers to an internal analytical and 
                      documentation framework that may support a borrower's financial presentation during 
                      independent negotiations.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-[#b8860b] text-sm tracking-wider mb-3">5.2 CHARACTERISTICS</h3>
                    <ul className="text-gray-400 space-y-2">
                      <li>• Informational and conditional in nature</li>
                      <li>• Non-binding and revocable</li>
                      <li>• Subject to third-party verification</li>
                      <li>• No assurance of acceptance or outcome</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-[#0a1628] border border-red-500/30 rounded">
                    <h3 className="text-red-400 text-sm tracking-wider mb-3">5.3 EXPLICIT LIMITATIONS</h3>
                    <p className="text-gray-400 mb-3">Credit enhancement documentation does not constitute:</p>
                    <ul className="text-gray-500 space-y-1 text-sm">
                      <li>• A guarantee or surety</li>
                      <li>• Insurance or risk transfer</li>
                      <li>• A promise to pay</li>
                      <li>• A financial obligation of the platform</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Section 6: Private Accord & PAS */}
          <section id="private-accord" className="mb-12">
            <h2 className="text-2xl font-light text-white mb-6 flex items-center gap-3">
              <span className="text-[#b8860b]">6.</span> Private Accord & PAS Documentation
            </h2>
            <div className="text-gray-300 leading-relaxed space-y-4">
              <p>
                Private Accord and Performance Acknowledgment Statements (PAS) are user-generated documents 
                intended to outline proposed terms, acknowledgments, or representations for consideration by third parties.
              </p>
              <Card className="bg-[#0a1628] border-[#2a3d5a]">
                <CardContent className="p-6">
                  <p className="text-white mb-4">These documents:</p>
                  <ul className="text-gray-400 space-y-2">
                    <li>• Are voluntary and non-binding unless expressly accepted by all parties</li>
                    <li>• Do not create enforceable obligations absent written creditor consent</li>
                    <li>• Are issued at the direction of the user</li>
                  </ul>
                  <p className="text-gray-500 text-sm mt-4 italic">
                    The platform does not deliver PAS documentation to creditors nor engage in creditor communications.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Section 7: Use Cases */}
          <section id="use-cases" className="mb-12">
            <h2 className="text-2xl font-light text-white mb-6 flex items-center gap-3">
              <span className="text-[#b8860b]">7.</span> Permitted and Prohibited Use Cases
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-[#0a1628] border-green-500/30">
                <CardContent className="p-6">
                  <h3 className="text-green-400 text-sm tracking-wider mb-4 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> 7.1 PERMITTED USES
                  </h3>
                  <ul className="text-gray-400 space-y-2 text-sm">
                    <li>• Preparation of documentation for borrower-led financial discussions</li>
                    <li>• Internal scenario modeling and recordkeeping</li>
                    <li>• Accredited investor and private commercial analysis</li>
                    <li>• Educational and informational review</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="bg-[#0a1628] border-red-500/30">
                <CardContent className="p-6">
                  <h3 className="text-red-400 text-sm tracking-wider mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> 7.2 PROHIBITED USES
                  </h3>
                  <ul className="text-gray-400 space-y-2 text-sm">
                    <li>• Marketing BETH as a debt settlement or payoff mechanism</li>
                    <li>• Representing BETH as currency or consideration</li>
                    <li>• Using BETH to satisfy, discharge, or extinguish obligations</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Section 8: Regulatory */}
          <section id="regulatory" className="mb-12">
            <h2 className="text-2xl font-light text-white mb-6 flex items-center gap-3">
              <span className="text-[#b8860b]">8.</span> Regulatory Considerations
            </h2>
            <Card className="bg-[#1a2d4a] border-[#2a3d5a]">
              <CardContent className="p-6">
                <p className="text-gray-300 mb-4">The BETH platform is structured to avoid classification as:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {['Money Services Business (MSB)', 'Payment Processor', 'Debt Settlement Provider', 'Credit Repair Organization'].map((item, i) => (
                    <div key={i} className="p-3 bg-[#0a1628] rounded text-center">
                      <span className="text-gray-400 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-gray-500 text-sm mt-4 italic">
                  Users remain solely responsible for compliance with applicable laws and regulations.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Section 9: Risk Factors */}
          <section id="risks" className="mb-12">
            <h2 className="text-2xl font-light text-white mb-6 flex items-center gap-3">
              <span className="text-[#b8860b]">9.</span> Risk Factors
            </h2>
            <Card className="bg-[#0a1628] border-[#b8860b]/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-[#b8860b] flex-shrink-0" />
                  <p className="text-gray-300">Use of the platform involves risks, including:</p>
                </div>
                <ul className="text-gray-400 space-y-2 ml-9">
                  <li>• No assurance of creditor acceptance</li>
                  <li>• Regulatory uncertainty affecting digital assets</li>
                  <li>• Technical limitations or failures</li>
                  <li>• Changes in applicable law or interpretation</li>
                </ul>
              </CardContent>
            </Card>
          </section>

          {/* Section 10: Governance */}
          <section id="governance" className="mb-12">
            <h2 className="text-2xl font-light text-white mb-6 flex items-center gap-3">
              <span className="text-[#b8860b]">10.</span> Governance and Controls
            </h2>
            <div className="text-gray-300 leading-relaxed">
              <p className="mb-4">Platform governance may include:</p>
              <ul className="text-gray-400 space-y-2 mb-4">
                <li>• Administrative oversight</li>
                <li>• Compliance monitoring</li>
                <li>• Smart contract maintenance and upgrades</li>
              </ul>
              <p className="text-gray-500 text-sm italic">Governance mechanisms may evolve over time.</p>
            </div>
          </section>

          {/* Section 11: Roadmap */}
          <section id="roadmap" className="mb-12">
            <h2 className="text-2xl font-light text-white mb-6 flex items-center gap-3">
              <span className="text-[#b8860b]">11.</span> Roadmap (Indicative)
            </h2>
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { phase: 'I', title: 'Core Documentation Engine', status: 'complete' },
                { phase: 'II', title: 'Audit & Reporting Enhancements', status: 'complete' },
                { phase: 'III', title: 'Permissioned Integrations', status: 'active' },
                { phase: 'IV', title: 'Expanded Compliance Tooling', status: 'upcoming' }
              ].map((item, i) => (
                <Card key={i} className={`bg-[#0a1628] border-[#2a3d5a] ${item.status === 'active' ? 'border-[#b8860b]' : ''}`}>
                  <CardContent className="p-4 text-center">
                    <div className={`text-2xl font-light mb-2 ${item.status === 'complete' ? 'text-green-500' : item.status === 'active' ? 'text-[#b8860b]' : 'text-gray-500'}`}>
                      Phase {item.phase}
                    </div>
                    <p className="text-gray-400 text-sm">{item.title}</p>
                    <div className={`mt-2 text-xs px-2 py-1 rounded inline-block ${
                      item.status === 'complete' ? 'bg-green-500/20 text-green-400' : 
                      item.status === 'active' ? 'bg-[#b8860b]/20 text-[#b8860b]' : 
                      'bg-gray-500/20 text-gray-500'
                    }`}>
                      {item.status === 'complete' ? 'Complete' : item.status === 'active' ? 'In Progress' : 'Upcoming'}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-gray-500 text-sm mt-4 italic text-center">All timelines are subject to change.</p>
          </section>

          {/* Section 12: Disclaimer */}
          <section id="disclaimer" className="mb-12">
            <h2 className="text-2xl font-light text-white mb-6 flex items-center gap-3">
              <span className="text-[#b8860b]">12.</span> Disclaimer
            </h2>
            <Card className="bg-red-900/20 border-red-500/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0" />
                  <p className="text-gray-300">
                    This white paper is provided for informational purposes only and does not constitute legal, 
                    financial, or investment advice. Nothing herein constitutes an offer, solicitation, or recommendation.
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Section 13: Conclusion */}
          <section id="conclusion" className="mb-12">
            <h2 className="text-2xl font-light text-white mb-6 flex items-center gap-3">
              <span className="text-[#b8860b]">13.</span> Conclusion
            </h2>
            <Card className="bg-[#1a2d4a] border-[#2a3d5a]">
              <CardContent className="p-8">
                <p className="text-gray-300 leading-relaxed text-lg">
                  BETH provides a structured, technology-enabled framework for private financial documentation 
                  while maintaining strict separation from payment, settlement, and regulated financial activities. 
                  The platform is designed to support transparency and auditability without assuming creditor-facing 
                  or fiduciary roles.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Footer Navigation */}
          <div className="flex justify-between items-center pt-8 border-t border-[#1a2d4a]">
            <Link to="/compliance">
              <Button variant="outline" className="border-[#2a3d5a] text-gray-400 hover:text-white">
                ← Compliance & Disclosures
              </Button>
            </Link>
            <Link to="/investor-guide">
              <Button className="bg-[#b8860b] hover:bg-[#9a7209]">
                Investor Guide →
              </Button>
            </Link>
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer className="border-t border-[#1a2d4a] bg-[#0d1d35] mt-16">
        <div className="max-w-7xl mx-auto px-6 py-8 text-center">
          <p className="text-gray-500 text-sm">
            © 2026 Bethereum Private Wealth Management. All rights reserved.
          </p>
          <p className="text-gray-600 text-xs mt-2">
            BETH Token Contract: 0x6822824c5349bf005e353ec5dda3fa34595ea811 | Ethereum Mainnet
          </p>
        </div>
      </footer>
    </div>
  );
};

export default WhitePaper;
